import React, { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { X, Shield, Leaf } from 'lucide-react';
import { useShellState } from '@/app/context/ShellContext';
import { usePermission } from '@/hooks/usePermission';
import { getNavigationForPortal } from '@/config/navigation.config';
import { cn } from '@/lib/utils';

export const MobileDrawer: React.FC = () => {
  const { isMobileDrawerOpen, setIsMobileDrawerOpen, activePortal } = useShellState();
  const { can, hasRole, user } = usePermission();
  const location = useLocation();

  useEffect(() => {
    setIsMobileDrawerOpen(false);
  }, [location.pathname, setIsMobileDrawerOpen]);

  if (!isMobileDrawerOpen) return null;

  const items = getNavigationForPortal(activePortal);

  return (
    <div className="fixed inset-0 z-50 flex lg:hidden font-heading">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-950/80 backdrop-blur-md transition-opacity animate-in fade-in"
        onClick={() => setIsMobileDrawerOpen(false)}
      />

      {/* Drawer Panel */}
      <div className="relative flex w-4/5 max-w-xs flex-col bg-sidebar text-sidebar-foreground shadow-2xl z-50 h-full p-4 space-y-4 font-poppins">
        <div className="flex items-center justify-between border-b border-sidebar-border pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] font-bold text-white text-xs shadow-sm">
              <Leaf className="h-4 w-4" />
            </div>
            <span className="font-extrabold text-sidebar-foreground text-sm">ProcureAI</span>
          </div>
          <button onClick={() => setIsMobileDrawerOpen(false)} className="rounded p-1 text-sidebar-foreground/70 hover:text-foreground cursor-pointer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 overflow-y-auto space-y-1.5 custom-scrollbar">
          {items.map((item) => {
            if (item.requiredRoles && !hasRole(item.requiredRoles)) return null;
            if (item.requiredPermissions && !item.requiredPermissions.some((p) => can(p))) return null;
            const Icon = item.icon;

            return (
              <NavLink
                key={item.id}
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3.5 rounded-xl px-3.5 py-2.5 text-xs font-semibold transition-all duration-200 relative group transform active:scale-98',
                    isActive
                      ? 'bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white font-extrabold shadow-md'
                      : 'text-sidebar-foreground/80 hover:bg-primary/10 hover:text-primary hover:translate-x-1'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className={cn('h-4 w-4 shrink-0 transition-transform duration-200', isActive ? 'text-white scale-110' : '')} />
                    <span className="font-bold">{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>

        {/* Footer Info */}
        <div className="border-t border-sidebar-border pt-3 text-xs flex items-center justify-between text-sidebar-foreground/60">
          <div className="flex items-center gap-1.5 truncate">
            <Shield className="h-3.5 w-3.5 text-primary shrink-0" />
            <span className="truncate">{user?.name}</span>
          </div>
          <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md uppercase">
            {user?.role}
          </span>
        </div>
      </div>
    </div>
  );
};
